import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Section } from '@/components/Section';
import { Button } from '@/components/ui/button';
import DOMPurify from 'dompurify';

const articles = {
  'restaurant-success-guide': {
    title: 'Cooking Up Success: A Deep Dive into "The Beginner\'s Guide to Restaurant Success"',
    subtitle: 'Practical wisdom for aspiring restaurateurs from Chef Robert "BB" Brunet',
    date: 'July 13, 2024',
    readTime: '12 min read',
    category: 'Entrepreneurship',
    color: '#F59E0B',
    content: `
In the dynamic and often challenging world of culinary entrepreneurship, having a reliable roadmap can make all the difference between thriving and merely surviving. Enter "The Beginner's Guide to Restaurant Success," a comprehensive manual compiled by Chef Robert "BB" Brunet that serves up a hearty helping of practical wisdom for aspiring restaurateurs.

## The Chef Behind the Book

Before we dive into the contents, it's worth taking a moment to appreciate the author's journey. Chef Robert "BB" Brunet's story is a testament to the power of passion and perseverance. After a 30-year career in Information Technology, Brunet made a bold decision at age 50 to "reboot" his life and pursue his culinary dreams.

Starting as "The House Chef of Colorado Springs," he eventually opened Momma Pearl's Cajun Kitchen, named after his mother. Brunet's experience of building a successful restaurant from the ground up—starting with just six tables and a "ramshackle kitchen"—lends authenticity and real-world relevance to the advice offered in this guide.

His journey embodies the very essence of his business philosophy: "Use What You've Got."

## A Comprehensive Guide to Restaurant Success

"The Beginner's Guide to Restaurant Success" is structured into 15 chapters, each focusing on a crucial aspect of restaurant management:

### 1. Menu Engineering
The journey begins with menu engineering, a critical aspect of any restaurant's success. This chapter delves into the art and science of creating a menu that not only tantalizes taste buds but also maximizes profitability.

### 2. Staff Training & Development
No restaurant can succeed without a well-trained, motivated team. This section emphasizes the importance of investing in your staff, providing them with the skills and knowledge they need to excel.

### 3. Efficient Kitchen Organization
A well-organized kitchen is the heart of any successful restaurant. This chapter explores strategies for optimizing kitchen layout, streamlining workflows, and implementing systems that ensure smooth operations.

### 4. Cost Control and Inventory Management
Controlling costs is crucial for maintaining profitability in the notoriously tight-margin restaurant industry.

### 5. Online Presence and Marketing
In today's digital age, a strong online presence is non-negotiable for restaurants.

### 6. Customer Relations and Satisfaction Management
Happy customers are the lifeblood of any successful restaurant.

### 7. Streamlining Operations with Technology
Technology can be a game-changer in restaurant management.

### 8. Food Safety and Sanitation Practices
Maintaining high standards of food safety and sanitation is crucial.

### 9. Restaurant Accounting and Tax Preparation
Understanding the financial side of the business is crucial for long-term success.

## Practical Wisdom from a Seasoned Restaurateur

What sets "The Beginner's Guide to Restaurant Success" apart is the practical, hands-on wisdom that Chef Brunet brings to the table. Throughout the book, he peppers the text with real-world examples and personal anecdotes.

> "Running a restaurant, like just about any business I suppose, is like pushing a snowball up a hill. The higher you push, the bigger and heavier the ball gets. You have to keep pushing or it will turn back on you and roll you over. Then you're done."

This vivid imagery encapsulates the continuous effort required to keep a restaurant thriving.

## Balancing Passion with Business Acumen

One of the key themes that emerges from the book is the importance of balancing culinary passion with sound business practices. Brunet emphasizes that while great food is essential, it's not enough on its own to ensure success.

He reminds readers that "IT IS A BUSINESS!!!" cautioning against the common pitfall of focusing solely on the culinary aspects while neglecting the business side of running a restaurant.

## Adapting to Challenges and Embracing Innovation

Another valuable aspect of the guide is its emphasis on adaptability and innovation. The restaurant industry is notoriously challenging and ever-changing, and Brunet's advice reflects this reality.

He encourages readers to be flexible, to learn from mistakes, and to continuously seek ways to improve their operations. This adaptability is exemplified in Brunet's own story of pivoting from his original plan to running a full-fledged restaurant.

## Defining Your Own Success

One of the most inspiring aspects of Brunet's guide is his emphasis on personal definition of success. He acknowledges that success in the restaurant industry can mean different things to different people.

For some, it might be about earning a living. For others, it could be about self-expression or artistic fulfillment. And for many, it's a combination of these factors.

> "If life ain't an adventure, what's the point?"

With this guide in hand, aspiring restaurateurs are well-equipped to embark on the exciting, challenging, and ultimately rewarding adventure of running a successful restaurant.
    `
  },
  'use-what-you-got-philosophy': {
    title: 'Unlocking Your Potential: A Deep Dive into "Use What You Got and Make a Lot"',
    subtitle: 'The power of resourcefulness, resilience, and determination',
    date: 'July 13, 2024',
    readTime: '15 min read',
    category: 'Philosophy',
    color: '#D4AF37',
    content: `
In a world often fixated on what we lack, Chef Robert "BB" Brunet's book "Use What You Got and Make a Lot" arrives as a refreshing reminder of the power of resourcefulness, resilience, and determination.

## The Man Behind the Message

Chef Robert "BB" Brunet's story is one of reinvention and perseverance. Born in Southern Louisiana as the tenth of thirteen siblings, Brunet's early life was steeped in the rich traditions of Cajun culture.

However, it wasn't until his 50s, after a successful career in Information Technology, that he decided to pursue his true passion—cooking. This dramatic career shift, prompted by the aftermath of Hurricane Katrina, led Brunet to relocate to Colorado Springs.

## The Philosophy of "Use What You Got"

At the heart of Brunet's book is a simple yet powerful philosophy: success isn't about waiting for the perfect circumstances or resources; it's about leveraging what you already have at your disposal.

> "I'm not waiting for my ship to come in, I'm building the damned thing myself."

This philosophy challenges readers to shift their perspective from what they lack to what they possess. It's about recognizing that we often have more resources, skills, and opportunities than we realize.

## The Structure of Success

"Use What You Got and Make a Lot" is thoughtfully organized into three main sections:

### Thoughts on Success

Brunet delves into the fundamental principles of achieving success. He emphasizes the importance of passion, resilience, and continuous learning. One of the most compelling ideas presented is the concept that "Success knows no calendar."

The author also addresses the fear of failure, a common obstacle for many aspiring entrepreneurs. Brunet reframes failure as a learning opportunity, encouraging readers to "Learn to embrace failure" and understand that "The road to success is paved with risk."

### Thoughts on Business

Drawing from his experiences as a restaurateur, Brunet offers valuable insights into entrepreneurship and business management. He presents the idea that "Marketing is the message; Advertising is the method."

One of the most intriguing ideas is captured in the phrase: "No one ever pays for their first 'joint'." While not advocating for anything illegal, Brunet uses this metaphor to discuss the importance of allowing potential customers to experience your product or service.

### Thoughts on Life Experience

Brunet shares wisdom gleaned from his rich life experiences. One of the most powerful ideas is encapsulated in the phrase:

> "God's greatest gift is the blank pages of our lives."

Brunet encourages readers to view their lives as a story yet to be written, empowering them to take control of their narrative and shape their own destiny.

## Practical Wisdom for Everyday Life

What sets "Use What You Got and Make a Lot" apart is its blend of inspirational philosophy and practical advice. Brunet doesn't just tell readers to chase their dreams; he provides concrete strategies for turning those dreams into reality.

> "Building Your Parachute on the way down."

This vivid metaphor encapsulates the idea that sometimes, the best way to learn is by doing. It encourages readers to take action, even if they don't feel fully prepared.

## A Holistic Approach to Success

What truly sets this book apart is its holistic approach to success. Brunet doesn't just focus on business strategies or personal development techniques. Instead, he presents a comprehensive philosophy that encompasses all aspects of life.

## A Call to Action

Perhaps the most powerful aspect of "Use What You Got and Make a Lot" is its call to action. Brunet challenges readers to apply these principles in their own lives:

> "Stop wanting and start becoming."

> "Turn thoughts into actions into accomplishments, then repeat."

Brunet understands that knowledge without action is futile, and he consistently pushes readers to translate his teachings into tangible results.

## Conclusion

In the end, "Use What You Got and Make a Lot" is a celebration of human potential. It's a reminder that each of us has something unique and valuable to offer the world. By embracing our strengths, learning from our experiences, and persistently working towards our goals, we can all create lives of meaning, purpose, and success.

We don't have to wait for our ship to come in—we can build it ourselves, using whatever materials we have at hand.
    `
  },
  'ai-survival-guide-humans': {
    title: 'Embracing the AI Revolution: A Human\'s Guide to Thriving in the Age of Artificial Intelligence',
    subtitle: 'Moving past fear toward a future of human-AI collaboration',
    date: 'July 1, 2024',
    readTime: '14 min read',
    category: 'AI & Technology',
    color: '#3B82F6',
    author: 'Dr. Synthia Speaks & R. Dale Brunet',
    content: `
In an era where artificial intelligence (AI) is rapidly transforming our world, it's natural to feel a mix of excitement and apprehension. Headlines often paint a picture of AI as a looming threat to jobs and privacy, or as an inscrutable black box making decisions beyond human comprehension.

But what if we're looking at it all wrong? What if AI, instead of being something to fear, is actually a powerful tool that can enhance our creativity, productivity, and quality of life?

This is the core message of "The Artificial Intelligence Survival Guide for: Humans," the groundbreaking book from Use What You Got Publishing. Written by the AI avatar Dr. Synthia Speaks and guided by the visionary entrepreneur R. Dale Brunet, this book offers a fresh perspective on AI.

## Understanding AI: Demystifying the Technology

At its core, AI is simply a set of technologies that enable computers to perform tasks that typically require human intelligence. This includes things like visual perception, speech recognition, decision-making, and language translation.

The book emphasizes that AI is not a singular, monolithic entity. Instead, it's a broad field encompassing various technologies and approaches, from machine learning and neural networks to natural language processing and computer vision.

## AI in Everyday Life: More Prevalent Than You Think

One of the eye-opening aspects of the book is its exploration of how AI is already integrated into our daily lives:

1. In our homes, through smart assistants like Alexa or Google Home
2. In our entertainment, curating personalized playlists and movie recommendations
3. In our workplaces, automating repetitive tasks and providing data-driven insights
4. In healthcare, assisting with diagnoses and treatment plans
5. In transportation, from navigation apps to the development of self-driving cars

## The Human-AI Partnership: Amplifying Our Potential

Perhaps the most powerful message is the idea of AI as a partner rather than a replacement for human intelligence. The book argues that the true potential of AI lies not in mimicking human cognition, but in complementing and enhancing it.

This partnership can take many forms:

- **Augmenting Creativity:** AI can handle routine tasks, freeing up human mental energy for more creative pursuits.
- **Enhancing Decision-Making:** By processing vast amounts of data and identifying patterns, AI can provide insights that inform human decision-making.
- **Personalizing Experiences:** AI can tailor experiences to individual preferences.
- **Improving Efficiency:** By automating repetitive tasks, AI allows humans to focus on higher-level thinking.
- **Expanding Human Capabilities:** AI can help us overcome limitations.

## Addressing the Fear Factor: Debunking AI Myths

The book tackles common fears and misconceptions:

1. **AI will make human workers obsolete:** While AI will change the job landscape, it will also create new opportunities.
2. **AI is too complex for ordinary people to understand:** The basics of AI are within reach for anyone willing to learn.
3. **AI decisions are unknowable "black boxes":** The growing field of explainable AI emphasizes transparency.
4. **AI will eventually surpass human intelligence and take over:** Fears of AI "taking over" are largely unfounded.

## Practical Steps for Embracing AI

The book offers practical advice:

1. **Develop AI Literacy:** Seek out resources and learn the basics of AI.
2. **Identify AI Opportunities:** Look for areas where AI could enhance efficiency.
3. **Start Small:** Begin with simple AI tools and gradually work your way up.
4. **Focus on Human Strengths:** Develop uniquely human skills like creativity and emotional intelligence.
5. **Stay Informed:** Keep up with AI developments.
6. **Engage in Ethical Discussions:** Help shape AI's responsible development.

## AI and Creativity: A New Frontier

Far from replacing human artists, AI is opening up new avenues for creative expression:

- In visual arts, AI can generate new images or styles as inspiration
- In music, AI can compose original melodies or provide accompaniment
- In writing, AI can help with generating ideas to editing prose
- In design, AI can quickly generate multiple options

The key point is that AI doesn't replace human creativity but augments it.

## Conclusion: Embracing the AI Revolution

The key message is one of empowerment. By understanding AI, developing relevant skills, and engaging with its development, we can shape this technology to serve human needs and values.

Our creativity, empathy, ethical reasoning, and ability to navigate complex social situations are uniquely human traits that AI cannot replicate. By embracing AI as a complementary force, we can amplify these human strengths.

> The choice is ours. Will we resist the tide of change, or will we ride the wave of innovation?
    `
  },
  'ai-survival-guide-small-business': {
    title: 'Unlocking the Power of AI for Small Businesses: A Comprehensive Guide',
    subtitle: 'How small businesses can leverage AI to compete with industry giants',
    date: 'July 1, 2024',
    readTime: '11 min read',
    category: 'AI & Technology',
    color: '#10B981',
    content: `
In today's rapidly evolving digital landscape, Artificial Intelligence (AI) is no longer the exclusive domain of tech giants and multinational corporations. Small businesses are increasingly recognizing the transformative potential of AI to level the playing field, enhance efficiency, and drive growth.

"The AI Survival Guide: for Small Business Owners" is a groundbreaking book that demystifies AI and provides practical insights for entrepreneurs looking to harness its power.

## Understanding AI: More Than Just Robots and Algorithms

The book begins by dispelling common myths about AI. It's not about creating sentient machines or replacing human workers. Instead, AI for small businesses is about leveraging intelligent software and data analysis to enhance decision-making, automate routine tasks, and provide personalized customer experiences.

## AI's Impact on Small Business Operations

### Customer Service and Engagement
AI-powered chatbots and virtual assistants can provide 24/7 customer support, handling routine inquiries and freeing up human staff for more complex issues. AI also enables personalizing customer interactions by analyzing data to predict preferences.

### Marketing and Sales
AI's potential to revolutionize marketing strategies is significant. Predictive analytics can help small businesses target their marketing efforts more precisely, increasing ROI and competing more effectively with larger competitors.

### Operations and Efficiency
From inventory management to supply chain optimization, AI can streamline various operational aspects. AI-driven predictive maintenance can reduce downtime and cut costs.

### Financial Management
AI can analyze financial data to provide deeper insights, forecast trends, and identify potential risks or opportunities.

## Navigating Challenges and Ethical Considerations

The authors provide practical advice on:

- **Data privacy and security:** Ensuring AI implementations comply with regulations
- **Bias in AI algorithms:** Recognizing and mitigating potential biases
- **Employee concerns:** Addressing fears about job displacement
- **Ethical AI deployment:** Implementing AI responsibly with transparency

## Practical Steps for AI Integration

The book outlines a step-by-step approach:

1. **Assessing Your Business Needs:** Identifying areas where AI can have the most impact
2. **Starting Small:** Beginning with pilot projects before full-scale implementation
3. **Choosing the Right Tools:** Evaluating AI vendors and solutions
4. **Building an AI-Ready Culture:** Preparing your team for AI integration
5. **Measuring Success:** Establishing KPIs to evaluate effectiveness

## Future-Proofing Your Business with AI

The authors emphasize that embracing AI is not just about keeping up with technology—it's about positioning your small business for long-term success in an increasingly digital and data-driven world.

## Why This Book Matters

1. **Accessibility:** Breaks down complex AI concepts into language non-technical business owners can understand
2. **Practicality:** Filled with real-world examples and actionable advice tailored for small businesses
3. **Comprehensiveness:** Covers AI applications across various business functions
4. **Ethical Focus:** Provides a balanced perspective on responsible AI adoption
5. **Future-Oriented:** Prepares readers for the evolving landscape of AI in business

## Conclusion

As AI continues to reshape the business landscape, small businesses that embrace this technology will have a significant advantage. This book provides the knowledge, tools, and strategies needed to navigate this new terrain confidently.

Whether you're a tech-savvy entrepreneur looking to take your AI implementation to the next level, or a small business owner just beginning to explore the possibilities of AI, this guide offers valuable insights.

> Don't just survive in the age of AI—learn how to thrive and lead in this exciting new era of small business innovation.
    `
  }
};

export default function InsightArticlePage() {
  const { slug } = useParams();
  const article = articles[slug];

  if (!article) {
    return (
      <main className="pt-20">
        <Section>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/insights">Back to Insights</Link>
            </Button>
          </div>
        </Section>
      </main>
    );
  }

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(article.title);

  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link 
              to="/insights" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
              data-testid="back-to-insights"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <span 
                className="text-sm px-3 py-1 rounded-full"
                style={{ backgroundColor: `${article.color}20`, color: article.color }}
              >
                {article.category}
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-testid="article-title">
              {article.title}
            </h1>

            <p className="text-xl text-muted-foreground mb-6">
              {article.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground pb-8 border-b border-border">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {article.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
              {article.author && (
                <span>By {article.author}</span>
              )}
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Content */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <motion.article
            className="prose prose-lg prose-invert max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div 
              className="article-content text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(article.content
                  .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-white mt-12 mb-4">$1</h2>')
                  .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-white mt-8 mb-3">$1</h3>')
                  .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-primary pl-6 my-6 text-xl text-slate-200 italic">$1</blockquote>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
                  .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
                  .replace(/\n\n/g, '</p><p class="mb-4">'))
              }}
            />
          </motion.article>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <span className="text-muted-foreground">Share this article:</span>
              <div className="flex items-center gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Related Articles CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-4">Continue Reading</h2>
          <p className="text-muted-foreground mb-6">
            Explore more essays on AI, entrepreneurship, and thought leadership.
          </p>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/insights">View All Insights</Link>
          </Button>
        </div>
      </Section>
    </main>
  );
}
